import { pascalCase } from 'change-case';
import React from 'react';

import type { Feature } from '../../types/features.enum';
import { useSession } from './useSession';

type FeatureFlagKey<K> = `is${Capitalize<K & string>}Enabled`;
export type FeatureFlags = {
  -readonly [K in keyof typeof Feature as FeatureFlagKey<K>]?: boolean;
};

export function useFeatureFlags() {
  const {
    state: { loggedInProfile },
  } = useSession();
  const [flags, setFlags] = React.useState<FeatureFlags>({});

  const hasFeature = React.useCallback(
    (feature: keyof typeof Feature) => {
      const features = (loggedInProfile?.featuresv2 ?? []).map((feat) => {
        const [scope, permission] = feat.split(':');
        return `${pascalCase(scope)}${pascalCase(permission)}`;
      });
      return features.includes(feature);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedInProfile?.user_id]
  );

  React.useEffect(() => {
    const flags = (loggedInProfile?.featuresv2 ?? []).reduce<FeatureFlags>(
      (acc, feature) => {
        const [scope, permission] = feature.split(':');
        const key = pascalCase(scope).concat(
          pascalCase(permission)
        ) as keyof typeof Feature;
        const flagKey: FeatureFlagKey<typeof key> = `is${key}Enabled`;
        acc[flagKey] = true;
        return acc;
      },
      {}
    );
    setFlags(flags);
  }, [loggedInProfile?.featuresv2, hasFeature]);

  return {
    flags,
    hasFeature,
  };
}
